import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { leadsAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Target, Clock, MapPin, User, Phone, Mail, Calendar,
  Building2, AlertCircle, CheckCircle, Loader2, RefreshCw,
  ChevronRight, Zap, Users, Award, Inbox,
} from 'lucide-react';

const URGENCY_STYLES = {
  new: { bg: 'bg-red-50', border: 'border-red-300', badge: 'bg-red-600', text: 'text-red-700', dot: 'bg-red-500 animate-pulse' },
  active: { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-500', text: 'text-amber-700', dot: 'bg-amber-400' },
  urgent: { bg: 'bg-red-100', border: 'border-red-400', badge: 'bg-red-700', text: 'text-red-800', dot: 'bg-red-600 animate-ping' },
  expired: { bg: 'bg-gray-50', border: 'border-gray-200', badge: 'bg-gray-400', text: 'text-gray-500', dot: 'bg-gray-400' },
};

export default function OpenLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noAccess, setNoAccess] = useState(false);
  const [acceptDialog, setAcceptDialog] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [successLead, setSuccessLead] = useState(null);
  const [tab, setTab] = useState('open');
  const [myAccepted, setMyAccepted] = useState([]);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leadsAPI.getOpenLeads();
      setLeads(res.data.leads || []);
      setNoAccess(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setNoAccess(true);
      } else {
        toast.error('Failed to load open leads');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMyAccepted = useCallback(async () => {
    try {
      const res = await leadsAPI.getMyAccepted();
      setMyAccepted(res.data.leads || []);
    } catch (err) {
      console.error('Failed to load accepted leads:', err);
    }
  }, []);

  useEffect(() => { loadLeads(); loadMyAccepted(); }, [loadLeads, loadMyAccepted]);

  const handleAccept = async () => {
    if (!acceptDialog) return;
    setAccepting(true);
    try {
      const res = await leadsAPI.acceptLead(acceptDialog.id);
      toast.success('Lead accepted successfully!');
      setSuccessLead({ ...acceptDialog, ...res.data });
      setAcceptDialog(null);
      loadLeads();
      loadMyAccepted();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error('This lead was just claimed by another agent');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to accept lead');
      }
    } finally {
      setAccepting(false);
    }
  };

  if (noAccess) {
    return (
      <div className="max-w-2xl mx-auto mt-20 text-center" data-testid="leads-no-access">
        <AlertCircle className="h-16 w-16 mx-auto text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 mb-2">Access Restricted</h2>
        <p className="text-gray-500">Your current rank does not have access to the Open Leads pool. Contact your admin for more information.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="open-leads-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Target className="h-7 w-7 text-teal-600" />
            Open Leads
          </h1>
          <p className="text-sm text-gray-500 mt-1">Unclaimed appointment requests from customers without agents</p>
        </div>
        <Button variant="outline" onClick={loadLeads} className="gap-2" data-testid="refresh-leads">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button onClick={() => setTab('open')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'open' ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid="tab-open">
          Open Leads {leads.length > 0 && <Badge className="ml-1 bg-red-600 text-white text-xs">{leads.length}</Badge>}
        </button>
        <button onClick={() => setTab('my')} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === 'my' ? 'border-teal-600 text-teal-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid="tab-my-accepted">
          My Accepted {myAccepted.length > 0 && <Badge className="ml-1 bg-teal-600 text-white text-xs">{myAccepted.length}</Badge>}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
        </div>
      ) : tab === 'open' ? (
        leads.length === 0 ? (
          <div className="text-center py-20" data-testid="no-open-leads">
            <Inbox className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No open leads right now</h3>
            <p className="text-sm text-gray-400 mt-1">Check back later for new appointment requests</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {leads.map(lead => {
              const style = URGENCY_STYLES[lead.urgency] || URGENCY_STYLES.active;
              return (
                <Card key={lead.id} className={`${style.bg} ${style.border} border-2 hover:shadow-md transition-shadow`} data-testid={`lead-card-${lead.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`h-2.5 w-2.5 rounded-full ${style.dot}`} />
                        <Badge className={`${style.badge} text-white text-xs`}>{lead.urgency_label}</Badge>
                      </div>
                      {lead.age_hours !== undefined && (
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {lead.age_hours < 1 ? `${Math.round(lead.age_hours * 60)}m` : `${lead.age_hours.toFixed(1)}h`} ago
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {lead.customer?.full_name || 'Customer'}
                    </h3>
                    <div className="space-y-1.5 text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                        <span className="truncate">{lead.property?.name || 'Property'}</span>
                      </div>
                      {lead.property?.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                          <span className="truncate">{lead.property.location}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Calendar className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                        <span>{lead.preferred_date || 'TBD'} {lead.preferred_time ? `- ${lead.preferred_time}` : ''}</span>
                      </div>
                      {lead.customer?.phone_masked && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                          <span>{lead.customer.phone_masked}</span>
                        </div>
                      )}
                    </div>
                    <Button className="w-full bg-teal-600 hover:bg-teal-700 gap-2" onClick={() => setAcceptDialog(lead)} data-testid={`accept-lead-${lead.id}`}>
                      <Zap className="h-4 w-4" /> Accept Lead
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )
      ) : (
        /* My Accepted Tab */
        myAccepted.length === 0 ? (
          <div className="text-center py-20" data-testid="no-accepted-leads">
            <Award className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-600">No accepted leads yet</h3>
            <p className="text-sm text-gray-400 mt-1">Accept open leads to grow your client base</p>
          </div>
        ) : (
          <div className="space-y-3">
            {myAccepted.map(lead => (
              <Card key={lead.id} className="border" data-testid={`accepted-lead-${lead.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center">
                      <User className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{lead.customer?.full_name || 'Customer'}</p>
                      <p className="text-sm text-gray-500">{lead.property?.name || 'Property'} - {lead.preferred_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {lead.customer?.phone && (
                      <a href={`tel:${lead.customer.phone}`} className="text-teal-600 hover:text-teal-700">
                        <Phone className="h-4 w-4" />
                      </a>
                    )}
                    {lead.customer?.email && (
                      <a href={`mailto:${lead.customer.email}`} className="text-teal-600 hover:text-teal-700">
                        <Mail className="h-4 w-4" />
                      </a>
                    )}
                    <Badge className="bg-teal-100 text-teal-700">Claimed</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )
      )}

      {/* Accept Confirmation Dialog */}
      <Dialog open={!!acceptDialog} onOpenChange={() => setAcceptDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="accept-dialog">
          <DialogHeader>
            <DialogTitle>Accept This Lead?</DialogTitle>
            <DialogDescription>
              {acceptDialog?.customer?.full_name} - {acceptDialog?.property?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2">
            <p className="font-medium text-gray-700">By accepting, you agree to:</p>
            <ul className="list-disc pl-5 text-gray-600 space-y-1">
              <li>This customer becomes your client permanently</li>
              <li>You handle this appointment and all follow-ups</li>
              <li>Future bookings from this customer are credited to you</li>
            </ul>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAcceptDialog(null)} data-testid="cancel-accept">Cancel</Button>
            <Button className="bg-teal-600 hover:bg-teal-700 gap-2" onClick={handleAccept} disabled={accepting} data-testid="confirm-accept">
              {accepting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
              Accept Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={!!successLead} onOpenChange={() => setSuccessLead(null)}>
        <DialogContent className="sm:max-w-md" data-testid="success-dialog">
          <div className="text-center py-4">
            <CheckCircle className="h-16 w-16 text-teal-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Lead Accepted!</h3>
            <p className="text-gray-600 mb-4">
              {successLead?.customer?.full_name || 'The customer'} is now your client
            </p>
            <div className="bg-gray-50 rounded-lg p-4 text-left text-sm space-y-2 mb-4">
              <p className="font-medium text-gray-700">Next Steps:</p>
              {(successLead?.next_steps || []).map((step, i) => (
                <div key={i} className="flex items-center gap-2 text-gray-600">
                  <ChevronRight className="h-3 w-3 text-teal-500" /> {step}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              {successLead?.customer?.phone && (
                <Button asChild className="flex-1 bg-teal-600 hover:bg-teal-700">
                  <a href={`tel:${successLead.customer.phone}`}><Phone className="h-4 w-4 mr-2" /> Call Now</a>
                </Button>
              )}
              <Button variant="outline" className="flex-1" onClick={() => setSuccessLead(null)}>Done</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
